import _snakeCase from 'lodash/snakeCase';

import {
    LaunchpadItem,
    LaunchpadSubtask,
    ChecklistSchemaItem,
    TaskStatus,
    FormationOwner,
} from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';

/**
 * Builds launchpad items from schema, always rendering all schema items
 * even if they're not returned by the API. Items not in API are marked as blocked.
 */

const DEFAULT_ACTIVE_STATUSES = [TaskStatus.IN_PROGRESS];

const VENDOR_FORMATION_OWNER: FormationOwner = 'VENDOR';

const isVendorOwned = (taskData?: LaunchpadItem) => taskData?.context?.formation_owner === VENDOR_FORMATION_OWNER;

const buildLaunchpadFromSchema = (
    schema: ChecklistSchemaItem[],
    launchpadTasks: Record<string, LaunchpadItem>
): LaunchpadItem[] => {
    const allTaskIds = new Set<string>();
    schema.forEach((schemaItem) => {
        allTaskIds.add(schemaItem.id);
        schemaItem.subtasks.forEach((subtask) => {
            if ('id' in subtask) {
                allTaskIds.add(subtask.id);
            } else if ('visual_dependencies' in subtask) {
                subtask.visual_dependencies.forEach((depId) => {
                    allTaskIds.add(depId);
                });
            }
        });
    });

    const allTaskData: Record<string, LaunchpadItem> = {};
    Array.from(allTaskIds).forEach((taskId) => {
        const taskData = launchpadTasks[taskId];
        if (taskData) {
            allTaskData[taskId] = taskData;
        }
    });

    return schema.map((schemaItem): LaunchpadItem => {
        if (schemaItem.subtasks.length > 0) {
            const subtasks = schemaItem.subtasks
                .map((subtask, index): LaunchpadSubtask => {
                    if ('id' in subtask) {
                        const subtaskData = allTaskData[subtask.id];
                        if (!subtaskData) {
                            // Subtask not in API - return blocked subtask with schema data
                            return {
                                id: subtask.id,
                                template_id: subtask.id,
                                name: subtask.title,
                                description: subtask.description,
                                status: TaskStatus.BLOCKED,
                                cta_url: undefined,
                                cta_label: undefined,
                                is_cta_external: false,
                                isFromApi: false,
                            };
                        }
                        return {
                            id: subtaskData.id,
                            template_id: subtask.id,
                            name: subtask.shouldReplaceInfo ? subtask.title : subtaskData.name,
                            description: subtask.shouldReplaceInfo ? subtask.description : subtaskData.description,
                            status: subtaskData.status,
                            cta_url: subtaskData.cta_url,
                            cta_label: subtaskData.cta_label,
                            is_cta_external: subtaskData.is_cta_external,
                            isFromApi: true,
                        };
                    }
                    if ('visual_dependencies' in subtask) {
                        const dependencyStatuses = subtask.visual_dependencies.map((depId) => {
                            const depData = allTaskData[depId];
                            if (depData || index < 1) {
                                return depData?.status || TaskStatus.PENDING;
                            }
                            // checks on the previous `subtask` status
                            const previousSubTask = schemaItem.subtasks.find((candidateSubtask, candidateIndex) => {
                                if (candidateIndex === index) {
                                    return false;
                                }
                                if ('id' in candidateSubtask) {
                                    return candidateSubtask.id === depId;
                                }
                                return false;
                            });
                            const previousSubTaskData =
                                previousSubTask && 'id' in previousSubTask ? allTaskData[previousSubTask.id] : null;
                            return previousSubTaskData?.status || TaskStatus.BLOCKED;
                        });

                        // Determine status based on dependencies
                        // Check if all dependencies are in the API
                        const allDepsInApi = subtask.visual_dependencies.every((depId) => !!allTaskData[depId]);
                        const hasActiveDependency = subtask.visual_dependencies.some((depId, dependencyIndex) => {
                            const activeStatuses =
                                subtask.vendorActiveStatuses && isVendorOwned(allTaskData[depId])
                                    ? subtask.vendorActiveStatuses
                                    : DEFAULT_ACTIVE_STATUSES;

                            return activeStatuses.includes(dependencyStatuses[dependencyIndex]);
                        });

                        let status = TaskStatus.BLOCKED;
                        if (allDepsInApi) {
                            if (dependencyStatuses.every((s) => s === TaskStatus.COMPLETED)) {
                                status = TaskStatus.COMPLETED;
                            } else if (hasActiveDependency) {
                                status = TaskStatus.IN_PROGRESS;
                            } else if (dependencyStatuses.some((s) => s === TaskStatus.BLOCKED)) {
                                status = TaskStatus.BLOCKED;
                            } else if (dependencyStatuses.some((s) => s === TaskStatus.PENDING)) {
                                status = TaskStatus.PENDING;
                            }
                        }

                        const facadeTemplateId = _snakeCase(subtask.title);
                        return {
                            id: facadeTemplateId,
                            template_id: facadeTemplateId,
                            name: subtask.title,
                            description: subtask.description,
                            status,
                            cta_url: undefined,
                            cta_label: undefined,
                            is_cta_external: false,
                            isFromApi: allDepsInApi,
                        };
                    }
                    return {
                        id: '',
                        template_id: '',
                        name: '',
                        description: '',
                        status: TaskStatus.BLOCKED,
                        cta_url: undefined,
                        cta_label: undefined,
                        is_cta_external: false,
                        isFromApi: false,
                    };
                })
                .filter((e) => e.id !== '');

            const allSubtasksCompleted = subtasks.every((subtask) => subtask.status === TaskStatus.COMPLETED);
            const anySubtaskFromApi = subtasks.some((subtask) => subtask.isFromApi);

            return {
                id: schemaItem.id,
                template_id: schemaItem.id,
                name: schemaItem.title ?? schemaItem.id.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                description: schemaItem.description ?? '',
                status: allSubtasksCompleted ? TaskStatus.COMPLETED : TaskStatus.PENDING,
                dependency: schemaItem.dependency,
                subtasks,
                allSubtasksCompleted,
                isFromApi: anySubtaskFromApi,
            };
        }

        const taskData = allTaskData[schemaItem.id];
        if (!taskData) {
            // Task not in API - return blocked task with schema data
            return {
                id: schemaItem.id,
                template_id: schemaItem.id,
                name: schemaItem.title ?? schemaItem.id.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                description: schemaItem.description ?? '',
                status: TaskStatus.BLOCKED,
                dependency: schemaItem.dependency,
                subtasks: [],
                cta_url: undefined,
                cta_label: undefined,
                is_cta_external: false,
                isFromApi: false,
            };
        }
        return {
            ...taskData,
            name: schemaItem.title ?? taskData.name,
            description: schemaItem.description ?? taskData.description,
            template_id: schemaItem.id,
            dependency: schemaItem.dependency,
            subtasks: [],
            isFromApi: true,
        };
    });
};

export default buildLaunchpadFromSchema;
